export const APP_NAME = 'C2 Logistics'

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  DELIVERIES: '/deliveries',
  DELIVERY_DETAIL: '/deliveries/:id',
  RETURNS: '/returns',
  RETURN_DETAIL: '/returns/:id',
  RETURN_CREATE_FROM_ERP: '/returns/new',
  RETURNS_REASON_SUMMARY: '/returns/reasons',
  RETURNS_VERIFICATION: '/returns/verification',
  RETURNS_VERIFICATION_DETAIL: '/returns/verification/:id',
  RETURNS_ERP_SETTINGS: '/returns/erp-settings',
  DELIVERY_REASONS: '/delivery-reasons',
  MAP_SETTINGS: '/map-settings',
  USERS: '/users',
  USER_DETAIL: '/users/:id',
  ERP: '/erp',
  ERP_PRODUCTS: '/erp/products',
  ERP_SALES: '/erp/sales',
  ERP_LOADS: '/erp/loads',
  ERP_LOAD_DETAIL: '/erp/loads/:id',
  ROUTES_PLANNING: '/routes',
  FLEET_DRIVERS: '/fleet/drivers',
  FLEET_VEHICLES: '/fleet/vehicles',
  CUSTOMERS: '/customers',
  ERP_SYNC: '/erp/sync',
  ERP_INVENTORY: '/erp/inventory',
  REPORTS: '/reports',
  REPORTS_NOT_RETURNED: '/reports/not-returned',
  REPORTS_ADMIN_CLOSED: '/reports/admin-closed',
  WAREHOUSE: '/warehouse',
  DRIVER: '/driver',
  DRIVER_LOADS: '/driver/loads',
  DRIVER_LOAD_DETAIL: '/driver/loads/:loadId',
  DRIVER_LOAD_ROUTE: '/driver/loads/:loadId/route',
  DRIVER_LOAD_STOP_ACTION: '/driver/loads/:loadId/stops/:stopId',
  DRIVER_PENDING_SYNC: '/driver/pendientes',
  DRIVER_PROFILE: '/driver/profile',
  DRIVER_NOTIFICATIONS: '/driver/notifications',
  DRIVER_DELIVERY: '/driver/delivery/:id',
  DRIVER_DELIVERY_RETURN: '/driver/delivery/:id/return',
} as const

export const QUERY_KEYS = {
  AUTH: {
    USER: ['auth', 'user'],
    SESSION: ['auth', 'session'],
  },
  DELIVERIES: {
    LIST: ['deliveries'],
    DETAIL: (id: string) => ['deliveries', id],
    STATS: ['deliveries', 'stats'],
    DRIVER_SUMMARY: ['deliveries', 'driver-summary'],
  },
  RETURNS: {
    LIST: ['returns'],
    DETAIL: (id: string) => ['returns', id],
    STATS: ['returns', 'stats'],
    PENDING_VERIFICATION: ['returns', 'pending-verification'],
    NOT_RETURNED: ['returns', 'not-returned'],
    REASON_SUMMARY: ['returns', 'reason-summary'],
    REASON_DETAIL: ['returns', 'reason-detail'],
    VERIFICATION_DETAIL: (id: string) => ['returns', 'verification', id],
    VERIFICATION_DRIVERS: ['returns', 'verification', 'drivers'],
    ITEM_BY_CODE: (returnId: string, code: string) =>
      ['returns', returnId, 'item-by-code', code] as const,
    ERP_DOCUMENTO_ID: ['returns', 'erp-settings', 'documento-id'],
  },
  WAREHOUSE: {
    ALL: ['warehouse'],
    WAITING: ['warehouse', 'waiting'],
    EVENTS: ['warehouse', 'events'],
  },
  USERS: {
    LIST: ['users'],
    DETAIL: (id: string) => ['users', id],
  },
  ERP: {
    PRODUCTS: ['erp', 'products'],
    SALES: ['erp', 'sales'],
    LOADS: ['erp', 'loads'],
    DRIVERS: ['erp', 'drivers'],
    VEHICLES: ['erp', 'vehicles'],
  },
  DASHBOARD: {
    STATS: ['dashboard', 'stats'],
    ALERTS: ['dashboard', 'alerts'],
  },
  PERFORMANCE: {
    ALL: ['performance'],
  },
} as const

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'c2_access_token',
  REFRESH_TOKEN: 'c2_refresh_token',
  USER: 'c2_user',
  THEME: 'c2_theme',
} as const

export const PAGINATION: {
  readonly DEFAULT_PAGE: number
  readonly DEFAULT_PAGE_SIZE: number
  readonly PAGE_SIZE_OPTIONS: number[]
} = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
}

export type PageNumber = number
export type PageSize = number

export const DELIVERY_STATUS = {
  PENDING: 'pending',
  ASSIGNED: 'assigned',
  DISPATCHED: 'dispatched',
  IN_TRANSIT: 'in_transit',
  DELIVERED: 'delivered',
  PARTIALLY_DELIVERED: 'partially_delivered',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const

export const RETURN_STATUS = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  PENDING_APPROVAL: 'pending_approval',
  APPROVED: 'approved',
  PENDING_VERIFICATION: 'pending_verification',
  VERIFIED: 'verified',
  REJECTED: 'rejected',
  CONFIRMED: 'confirmed',
  CONFIRMED_TO_ERP: 'confirmed_to_erp',
  ERROR_CONFIRMING: 'error_confirming',
  CANCELLED: 'cancelled',
} as const

// Códigos de rol de la base de seguridad (FacturacionAI, dbo.Roles.Codigo).
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  DEVELOPER: 'desarrollador',
  SUPERVISOR: 'supervisor',
  OPERATOR: 'operario',
  DRIVER: 'conductor',
  VIEWER: 'viewer',
  // Alias de los nombres anteriores
  LOGISTICS_MANAGER: 'supervisor',
  DISPATCHER: 'operario',
  WAREHOUSE: 'operario',
} as const

/** Roles que operan el backoffice (cualquiera de ellos anula el modo "solo conductor"). */
export const STAFF_ROLES: readonly string[] = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.DEVELOPER,
  ROLES.SUPERVISOR,
  ROLES.OPERATOR,
]

/** Permisos usados directamente en la UI (formato "modulo:accion" de dbo.Permisos.Codigo). */
export const PERMISSIONS = {
  RETURNS_MANUAL_QUANTITY: 'devoluciones:verificar_cantidad_manual',
  /** Registrar entregas (total/parcial) como consignación; se asigna por rol. */
  DELIVERY_CONSIGNMENT: 'entregas:consignacion',
  /** Marcar "Llegué a la bodega" en la app del conductor; se asigna por rol (o a un usuario). */
  WAREHOUSE_ARRIVAL: 'bodega:marcar_llegada',
  /** Revertir a Pendiente una factura cerrada por el conductor. */
  DELIVERY_REVERT_TO_PENDING: 'entregas:revertir_pendiente',
  /** Finalizar (cierre administrativo) un cargue que el conductor no terminó. */
  LOAD_ADMIN_CLOSE: 'cargues:cierre_administrativo',
} as const

export type DeliveryStatus = (typeof DELIVERY_STATUS)[keyof typeof DELIVERY_STATUS]
export type ReturnStatus = (typeof RETURN_STATUS)[keyof typeof RETURN_STATUS]
export type Role = (typeof ROLES)[keyof typeof ROLES]
